import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import {
  ActivatedRoute,
  Data,
  Params,
  Router,
  RouterLink,
} from '@angular/router';
import { CardImageComponent } from '@components/card-image/card-image.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpParams, httpResource } from '@angular/common/http';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { MapArchiveAddMapFormComponent } from '../add-form/form.component';
import { MapArchiveFilterComponent } from '@components/map/archive/list/filter/dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ContributorRole,
  MapArchiveEntry,
  MapArchiveListResponse,
  MapEntryDetails,
  MapOrgRole,
  Permission,
  PublicationType,
  User,
  GeoJSONToWKT,
  WKTToGeoJSON
} from '@models';
import { AuthService, MapArchiveService, NotificationService } from '@services';

import { environment } from '@environment';

@Component({
  selector: 'app-map-archive-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    CardImageComponent,
    CommonModule,
    FormsModule,
    LeafletGroupComponent,
    ListComponent,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    RouterLink,
  ],
})
export class MapArchiveListComponent {
  publicationTypes = PublicationType;

  // Environment Variables
  kinko = environment.kinkoHost;
  mapDataset = environment.mapArchiveDatasetId;

  // Base URLs to fetch web preview and thumbnail
  thumbBaseAddr =
    this.kinko +
    '/dataset/' +
    this.mapDataset +
    '/attachment/_blob' +
    '?prefix=/processed/thumbnail/' +
    '&skip=0&take=1&count=true&order=created:asc';

  // Enums
  contribRole = ContributorRole;
  orgRole = MapOrgRole;

  // Router
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Services
  authService = inject(AuthService);
  mapArchiveService = inject(MapArchiveService);
  notificationService = inject(NotificationService);

  // Permissions
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  // Dialog
  dialog = inject(MatDialog);

  // Pagination
  defaultPageSize = 12;
  pageSizeOptions = [this.defaultPageSize, 24, 48, 96];
  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || this.defaultPageSize);

  // User
  user = signal<User | null | undefined>(undefined);

  // Search
  search = signal<string>(this.route.snapshot.queryParams['q'] || '');
  mapExpanded = signal<boolean>(false);

  // Attribute Filters
  filters = signal<string | string[] | undefined>(this.route.snapshot.queryParams['filter']);
  parsedFilters = computed(() => {
    const f = this.filters();
    if (Array.isArray(f)) {
      return f as string[];
    } else return f ? [f as string] : [];
  });

  // Location filter
  location = signal<string | undefined>(this.route.snapshot.queryParams['location']);
  geojson = computed(() => WKTToGeoJSON(this.location()));

  // Result list
  count = signal(0);
  mapList = httpResource<MapArchiveListResponse>(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    let httpParams = new HttpParams()
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize())
      .set('count', count)
      .set('order', 'modified:desc')
      .set('q', this.search() || '')

    for (const f of this.parsedFilters()) {
      httpParams = httpParams.set('filter', f);
    }

    const location = this.location();
    if (location) {
      httpParams = httpParams.set('location', location);
    }

    return `${environment.kinkoHost}/dataset/${this.mapDataset}/record/?${httpParams.toString()}`;
  })

  constructor() {
    // Return 404 page if no map archive dataset is configured.
    if (this.mapDataset === '') {
      this.router.navigate(['/404'], {
        skipLocationChange: true,
      });
      this.notificationService.error('No map archive dataset configured.');
    }

    // Resolve dataset permissions on navigation
    this.route.data.subscribe((data: Data) => {
      this.permissions.set(data['permissions']);
    });

    // subscribe to Route params
    this.route.queryParams.subscribe((params: Params) => {
      this.search.set(params['q'] as string || '');
      this.location.set(params['location']);
      this.page.set(parseInt(params['page']) || 0);
      this.pageSize.set(parseInt(params['pageSize']) || this.defaultPageSize);
      this.filters.set(params['filter']);

      if (this.location()) {
        this.mapExpanded.set(true);
      } else {
        this.mapExpanded.set(false);
      }
    });

    effect(() => {
      if (!this.mapList.hasValue() || !this.mapList.value().count) {
        return;
      }

      this.count.set(this.mapList.value().count);
    });
  }

  onPage(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: this.search() || undefined,
        page: event.pageIndex || undefined,
        pageSize:
          event.pageSize === this.defaultPageSize ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }

  onSearch(event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: (event.target as HTMLInputElement).value || '',
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  onGeoSearch() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: this.search(),
        location: undefined,
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  advancedSearch() {
    const filter = this.parsedFilters().length ? this.parsedFilters()[0].split(':')[1] : undefined;
    this.dialog.open(MapArchiveFilterComponent, {
      data: {
        mapType: filter,
        geojson: this.geojson(),
        pubLang: '',
        pubCountry: '',
      },
      minWidth: '360px',
      maxWidth: '960px',
    }).afterClosed().subscribe(
      (data: {
        mapType: string,
        geojson: GeoJSON.FeatureCollection | undefined;
        pubLang: string,
        pubCountry: string,
      } | undefined) => {
        if (!data) {
          return;
        }

        let location: string | undefined = undefined;
        if (data.geojson?.features.length) {
          location = GeoJSONToWKT(data.geojson);
        }

        this.router.navigate([], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams: {
            q: this.search(),
            location: location,
            filter: data.mapType ? `mapType:${data.mapType}` : undefined,
            page: undefined,
          },
          replaceUrl: true,
        });
      }
    );
  }

  onFilterClear() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: this.search(),
        filter: undefined,
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  // Open dialog box to register a new map
  onAddMapClick() {
    this.dialog.open(MapArchiveAddMapFormComponent, {
      minWidth: '360px',
      width: '480px',
    }).afterClosed().subscribe((title: string) => {
      if (!title) {
        return;
      }

      this.mapArchiveService.addMap(title).subscribe({
        next: (map: MapArchiveEntry) => {
          this.router.navigate([map.id, 'edit'], { relativeTo: this.route });
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to add new map to archive.');
        },
      });
    });
  }

  // Look for publishers in the contributor and organisation lists.
  getPublisher(map: MapArchiveEntry): string {
    if (map.content.contributors) {
      for (const contrib of map.content.contributors) {
        if (
          map.content.scans &&
          contrib.roles.includes(this.contribRole['Publisher'])
        ) {
          return contrib.name;
        }
      }
    }

    if (map.content.organisations) {
      for (const org of map.content.organisations) {
        if (
          map.content.scans &&
          org.roles.includes(this.orgRole['Publisher'])
        ) {
          return org.name;
        }
      }
    }

    return 'Unknown';
  }

  // Returns a string <year> || <lower bound year>..<higher bound year>.
  getPublicationRange(entry: MapEntryDetails): string {
    let lower = 0;
    let upper = 0;
    let year = 0;

    if (entry.scans) {
      for (const img of Object.keys(entry.scans)) {
        if (entry.scans[img]) {
          year = entry.scans[img].publicationYear || 0;
        }
        if (year !== 0) {
          if (lower === 0 || year < lower) {
            lower = year;
          }
          if (upper === 0 || year > upper) {
            upper = year;
          }
        }
      }
    }

    if (lower === 0 && upper === 0) {
      return '';
    }
    if (lower != 0 && lower == upper) {
      return String(lower);
    }

    return lower + '..' + upper;
  }
}
